import {
  SynchronousBindingOptions,
  useSynchronousBindingLazy,
} from './synchronous.binding';

/**
 * Robot operation types that can be executed via RobotOperationBinding
 * This mirrors RobotOperationType from cloud-connector
 */
export enum RobotOperationType {
  EXECUTE_FLOW = 'execute_flow',
  MOVE_TO_POINT = 'move_to_point',
}

/**
 * Robot operation feedback types
 * This mirrors RobotOperationFeedbackType from cloud-connector
 */
export enum RobotOperationFeedbackType {
  STATUS = 'status',
  EVENT = 'event',
}

/**
 * Context for robot operation binding
 * Maps to cloud-connector RobotOperationBindingContext
 */
export interface RobotOperationBindingContext {
  operationType: RobotOperationType;
  operationRequest: unknown;
}

/**
 * Result from robot operation binding
 * Maps to cloud-connector RobotOperationBindingResult
 */
export type RobotOperationBindingResult<
  T extends RobotOperationType = RobotOperationType,
  F extends RobotOperationFeedbackType = RobotOperationFeedbackType,
  ResultData = Record<string, unknown>,
  FeedbackData = Record<string, unknown>,
> =
  | RobotOperationBindingFeedbackResult<T, F, FeedbackData>
  | RobotOperationBindingSuccessResult<T, ResultData>
  | RobotOperationBindingFailureResult<T>
  | RobotOperationBindingCanceledResult<T>;

export interface RobotOperationBindingFeedbackResult<
  T extends RobotOperationType = RobotOperationType,
  F extends RobotOperationFeedbackType = RobotOperationFeedbackType,
  FeedbackData = Record<string, unknown>,
> {
  done: false;
  operationType: T;
  feedbackType: F;
  feedbackData: FeedbackData;
}

export interface RobotOperationBindingSuccessResult<
  T extends RobotOperationType = RobotOperationType,
  ResultData = Record<string, unknown>,
> {
  done: true;
  success: true;
  operationType: T;
  operationResult: ResultData;
}

export interface RobotOperationBindingFailureResult<
  T extends RobotOperationType = RobotOperationType,
> {
  done: true;
  success: false;
  operationType: T;
  errorMessage: string; // property will be non-blank
}

export interface RobotOperationBindingCanceledResult<
  T extends RobotOperationType = RobotOperationType,
> {
  done: true;
  success: false;
  operationType: T;
  errorMessage?: ''; // property will be blank
}

export type RobotOperationBindingExecuteFlowStatusFeedbackResult =
  RobotOperationBindingFeedbackResult<
    RobotOperationType.EXECUTE_FLOW,
    RobotOperationFeedbackType.STATUS,
    { executionIndex: number }
  >;

export const isExecuteFlowStatusFeedbackResult = (
  result?: RobotOperationBindingResult | null
): result is RobotOperationBindingExecuteFlowStatusFeedbackResult => {
  return (
    isFeedbackResult(result) &&
    result.operationType === RobotOperationType.EXECUTE_FLOW &&
    result.feedbackType === RobotOperationFeedbackType.STATUS
  );
};

export const isFeedbackResult = (
  result?: RobotOperationBindingResult | null
): result is RobotOperationBindingFeedbackResult => {
  return !!(result && !result.done);
};

export const isSuccessResult = (
  result?: RobotOperationBindingResult | null
): result is RobotOperationBindingSuccessResult => {
  return !!(result && result.done && result.success);
};

export const isErrorResult = (
  result?: RobotOperationBindingResult | null
): result is RobotOperationBindingFailureResult => {
  return !!(result && result.done && !result.success && result.errorMessage);
};

export const isCanceledResult = (
  result?: RobotOperationBindingResult | null
): result is RobotOperationBindingCanceledResult => {
  return !!(result && result.done && !result.success && !result.errorMessage);
};

/**
 * Lazy hook for executing robot operations via RobotOperationBinding
 * This provides a lower-level interface compared to useRobotOperations()
 *
 * Use this when you need direct control over the binding lifecycle
 * For normal operations, use useRobotOperations() instead
 */
export const useRobotOperationBindingLazy = <
  RobotOperationBindingMeta = undefined,
>(
  options?: Pick<
    SynchronousBindingOptions<
      RobotOperationBindingResult,
      RobotOperationBindingMeta
    >,
    'closeImmediately' | 'onResult'
  >
) => {
  const { send, close } = useSynchronousBindingLazy<
    RobotOperationBindingContext,
    RobotOperationBindingResult,
    RobotOperationBindingMeta
  >({
    namespace: 'machine',
    type: 'robotOperation',
    closeImmediately: options?.closeImmediately ?? true,
    onResult: options?.onResult,
  });

  return { send, close };
};
